import type { IconProps } from '@phosphor-icons/react'
import { forwardRef } from 'react'
import { cn } from './primitives'

/**
 * Courier-company marks (24-unit grid), coloured like FinanceIcons / MaterialsIcons so the partner is recognised
 * before its name is read. Phosphor-compatible (size / weight / className) for the navigation, PageHeader and cards.
 */
export type IntegrationIconKey = 'lineExpress' | 'attemptsAlert' | 'notificationsHub'

/** Line Express's own brand colour (Companies/Free/180109 → BasicColor #4654AC). */
export const INTEGRATION_ICON_TONE: Record<IntegrationIconKey, { fg: string; bg: string; accent: string }> = {
  lineExpress: { fg: '#4654AC', bg: '#eceefa', accent: '#f59e0b' },
  /** Failed delivery attempts → closing as a return: an amber parcel, a return arrow and a red alert badge. */
  attemptsAlert: { fg: '#d97706', bg: '#fff4e0', accent: '#dc2626' },
  /** Notifications & alerts hub: a teal bell, a WhatsApp-green chat bubble and a red "new" dot. */
  notificationsHub: { fg: '#0f766e', bg: '#e3f5f2', accent: '#16a34a' },
}

const S = { strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

function paths(key: IntegrationIconKey) {
  const { fg, accent } = INTEGRATION_ICON_TONE[key]
  switch (key) {
    case 'lineExpress':
      // a delivery van running along speed lines: "line" + "express"
      return (<>
        <path d="M8 7.5h7.2a1 1 0 0 1 1 1V16H8V7.5Z" fill={fg} opacity=".2" stroke={fg} {...S} />
        <path d="M16.2 10.5h2.6l2.2 2.8V16h-4.8v-5.5Z" fill={fg} stroke={fg} {...S} />
        <path d="M17.6 11.6h1l1.1 1.5h-2.1v-1.5Z" fill="#fff" />
        <circle cx="10.6" cy="16.6" r="1.8" fill="#fff" stroke={fg} strokeWidth="1.6" />
        <circle cx="18.4" cy="16.6" r="1.8" fill="#fff" stroke={fg} strokeWidth="1.6" />
        <path d="M2.5 9.5h3.8M1.5 12h4.8M3 14.5h3.3" stroke={accent} strokeWidth="1.7" strokeLinecap="round" />
      </>)
    case 'attemptsAlert':
      // a parcel with a return arrow on its face and a red "!" badge: failed attempts, going back
      return (<>
        <path d="M3 10.2 10.5 7l7.5 3.2v7.3l-7.5 3.3L3 17.5v-7.3Z" fill={fg} opacity=".18" />
        <path d="M3 10.2 10.5 7l7.5 3.2v7.3l-7.5 3.3L3 17.5v-7.3Z" stroke={fg} {...S} />
        <path d="M3 10.2l7.5 3.2 7.5-3.2M10.5 13.4v7.4" stroke={fg} {...S} />
        <path d="M16 16.2h-2.6a1.6 1.6 0 0 1 0-3.2h.9" stroke={fg} {...S} />
        <path d="m14.9 17.3-1.2-1.1 1.2-1.1" stroke={fg} {...S} />
        <circle cx="18.6" cy="5.6" r="3.9" fill={accent} />
        <path d="M18.6 3.8v2.3" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="18.6" cy="7.6" r=".9" fill="#fff" />
      </>)
    case 'notificationsHub':
      // a bell (in-app alerts) with a green chat bubble (WhatsApp) and a red dot: every message and alert in one place
      return (<>
        <path d="M4.5 16.5c1-1.1 1.6-2.3 1.6-4.2V10a5 5 0 0 1 10 0v2.3c0 1.9.6 3.1 1.6 4.2H4.5Z" fill={fg} opacity=".2" />
        <path d="M4.5 16.5c1-1.1 1.6-2.3 1.6-4.2V10a5 5 0 0 1 10 0v2.3c0 1.9.6 3.1 1.6 4.2H4.5Z" stroke={fg} {...S} />
        <path d="M9.3 19.2a2 2 0 0 0 3.6 0" stroke={fg} {...S} />
        <path d="M11.1 5V3.6" stroke={fg} {...S} />
        <path d="M15.2 13.4a3.9 3.9 0 1 1 1.4 3l-1.9.5.5-1.8a3.9 3.9 0 0 1 0-1.7Z" fill={accent} stroke="#fff" strokeWidth="1.1" strokeLinejoin="round" transform="translate(1.6 -1.2)" />
        <path d="M18.3 13.1h2.4M18.3 14.6h1.6" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" transform="translate(-.2 -1.2)" />
        <circle cx="17.6" cy="4.8" r="2.3" fill="#dc2626" stroke="#fff" strokeWidth="1" />
      </>)
  }
}

function make(key: IntegrationIconKey) {
  const Mark = forwardRef<SVGSVGElement, IconProps>(function IntegrationMark({ size = 16, className, weight: _w, mirrored: _m, alt, color: _c, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} {...rest}>
        {paths(key)}
      </svg>
    )
  })
  Mark.displayName = `IntegrationIcon.${key}`
  return Mark
}

/** Phosphor-compatible components: `<INTEGRATION_ICONS.lineExpress size={20} />`. */
export const INTEGRATION_ICONS = Object.fromEntries((Object.keys(INTEGRATION_ICON_TONE) as IntegrationIconKey[]).map((k) => [k, make(k)])) as Record<IntegrationIconKey, ReturnType<typeof make>>

/** The mark on its tinted square (page headers, cards). */
export function IntegrationIconTile({ name, size = 40 }: { name: IntegrationIconKey; size?: number }) {
  const I = INTEGRATION_ICONS[name]
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-xl" style={{ width: size, height: size, background: INTEGRATION_ICON_TONE[name].bg }}>
      <I size={Math.round(size * 0.62)} />
    </span>
  )
}
